import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Check, ShieldCheck, FileText, Building2, Scale } from "lucide-react";
import { LEGAL_SELLER, TECH_ENTITY } from "@/lib/brand";
import { useParallax } from "@/hooks/use-reveal";
import { AuroraCanvas } from "@/components/AuroraCanvas";

const HERO_PHOTO = "https://images.unsplash.com/photo-1564069114553-7215e1ff1890?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const TRUST = [
    { icon: Building2, t: "Formation to funding, one team" },
    { icon: FileText, t: "Scope & fees in writing first" },
    { icon: ShieldCheck, t: "Human review on every SI workflow" },
    { icon: Scale, t: "Licensed partners where required" },
];

function ReadinessWidget() {
    return (
        <div className="si-hero-widget si-depth-2 w-[300px] rounded-2xl p-5 sm:w-[340px]" data-testid="hero-widget" aria-hidden="true">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[13px] font-bold text-white"><span className="si-live-dot" /> Launch progress</div>
                <span className="si-example-dark">Illustrative</span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
                {[["Formed", true], ["Compliant", true], ["Funded", false]].map(([t, done]) => (
                    <div key={t} className="rounded-lg border border-white/10 bg-white/[0.06] px-2.5 py-2 text-[11px] font-semibold text-slate-200">
                        <div className="flex items-center gap-1.5">{done ? <Check className="h-3 w-3 text-teal-light" /> : <span className="h-2 w-2 animate-pulse rounded-full bg-teal-light motion-reduce:animate-none" />}{t}</div>
                    </div>
                ))}
            </div>
            <svg viewBox="0 0 320 110" preserveAspectRatio="none" className="mt-4 h-[110px] w-full">
                <defs>
                    <linearGradient id="hl" x1="0" x2="1"><stop offset="0" stopColor="#2F7BD1" /><stop offset="1" stopColor="#14B8A6" /></linearGradient>
                    <linearGradient id="hf" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="rgba(20,184,166,.35)" /><stop offset="1" stopColor="rgba(20,184,166,0)" /></linearGradient>
                </defs>
                {[28, 56, 84].map((y) => <line key={y} x1="0" x2="320" y1={y} y2={y} stroke="rgba(255,255,255,.08)" strokeDasharray="3 6" />)}
                <path className="si-dash-fill" d="M0 95 C 40 92, 70 80, 100 78 S 160 58, 190 50 S 260 26, 320 12 L320 110 L0 110Z" fill="url(#hf)" />
                <path className="si-dash-line" d="M0 95 C 40 92, 70 80, 100 78 S 160 58, 190 50 S 260 26, 320 12" fill="none" stroke="url(#hl)" strokeWidth="2.5" strokeLinecap="round" pathLength="1" />
                <circle className="si-dash-dot" cx="320" cy="12" r="4.5" fill="#14B8A6" />
            </svg>
            <div className="mt-2 flex items-center justify-between font-mono text-[10.5px] uppercase tracking-wider text-slate-400">
                <span>Milestones · LLC → grants → first hire</span><span className="text-teal-light">Human review on</span>
            </div>
        </div>
    );
}

export function HomeHero({ onPath }) {
    const navigate = useNavigate();
    const heroRef = useRef(null);
    useParallax(heroRef);
    return (
        <>
            <section ref={heroRef} id="home" className="si-hero-dark relative overflow-hidden pt-32 pb-24 text-white lg:pt-40 lg:pb-32" data-testid="home-hero">
                <AuroraCanvas dark className="si-hero-aurora" />
                <div className="si-hero-photo" aria-hidden="true"><img src={HERO_PHOTO} alt="" loading="eager" fetchpriority="high" /></div>
                <div className="si-hero-scrim" aria-hidden="true" />
                <div className="si-gridlines si-gridlines-dark" aria-hidden="true" />
                <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
                    <div>
                        <div className="si-pill-dark reveal"><span className="si-pill-dot" /> Smarter Choice Investment Holdings · Technology by {TECH_ENTITY}</div>
                        <h1 className="reveal mt-7 font-display text-[44px] font-semibold leading-[1.02] tracking-[-0.038em] sm:text-6xl lg:text-[76px]" style={{ "--d": "80ms" }} data-testid="hero-headline">
                            The business partner<br /><span className="font-serif-accent text-teal-light pr-2">you’ve been missing.</span>
                        </h1>
                        <p className="reveal mt-6 max-w-xl text-lg leading-relaxed text-slate-300 sm:text-xl" style={{ "--d": "160ms" }} data-testid="hero-lead">
                            Formation, compliance, IP protection, grants and capital readiness, real estate, and estate planning — delivered by one team and powered by Smarter Intelligence.
                        </p>
                        <div className="reveal mt-9 flex flex-col gap-3 sm:flex-row" style={{ "--d": "240ms" }}>
                            <button onClick={() => onPath("paths")} data-testid="hero-get-started" className="si-btn-primary group">Explore services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></button>
                            <button onClick={() => navigate("/grants")} data-testid="hero-talk-si" className="si-btn-ghost-dark"><Sparkles className="mr-2 h-4 w-4 text-teal-light" /> Find grants</button>
                        </div>
                        <p className="reveal mt-6 text-[13px] text-slate-400" style={{ "--d": "300ms" }}>Operated by {LEGAL_SELLER}. Not a law firm or lender — licensed partners engaged where required.</p>
                    </div>
                    <div className="reveal flex justify-center lg:justify-end" style={{ "--d": "200ms" }}><ReadinessWidget /></div>
                </div>
            </section>

            <section className="border-b border-slate-200 bg-white" data-testid="trust-strip">
                <div className="mx-auto grid max-w-7xl grid-cols-2 gap-x-6 gap-y-4 px-5 py-6 lg:grid-cols-4 lg:px-8">
                    {TRUST.map(({ icon: I, t }) => (
                        <div key={t} className="flex items-center gap-3 text-[13.5px] font-semibold text-navy"><I className="h-4.5 w-4.5 shrink-0 text-teal" />{t}</div>
                    ))}
                </div>
            </section>
        </>
    );
}
